import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RegistryCard = ({ registry, onForceSync, onViewLogs, onConfigure }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success bg-success/10 border-success/20';
      case 'syncing':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'error':
        return 'text-error bg-error/10 border-error/20';
      case 'disconnected':
        return 'text-muted-foreground bg-muted border-border';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle';
      case 'syncing':
        return 'RefreshCw';
      case 'error':
        return 'AlertCircle';
      case 'disconnected':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const formatDataVolume = (volume) => {
    if (volume >= 1000000) {
      return `${(volume / 1000000)?.toFixed(1)}M`;
    } else if (volume >= 1000) {
      return `${(volume / 1000)?.toFixed(1)}K`;
    }
    return volume?.toString();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-subtle transition-smooth">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name={registry?.icon} size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">{registry?.name}</h3>
            <p className="text-sm text-muted-foreground">{registry?.type}</p>
          </div>
        </div>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getStatusColor(registry?.status)}`}>
          <Icon 
            name={getStatusIcon(registry?.status)} 
            size={14} 
            className={registry?.status === 'syncing' ? 'animate-spin' : ''} 
          />
          <span className="text-xs font-medium capitalize">{registry?.status}</span>
        </div>
      </div>
      {/* Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="text-xs text-muted-foreground mb-1">Last Sync</div>
          <div className="text-sm font-medium text-card-foreground">{registry?.lastSync}</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="text-xs text-muted-foreground mb-1">Data Volume</div>
          <div className="text-sm font-medium text-card-foreground">
            {formatDataVolume(registry?.dataVolume)} credits
          </div>
        </div>
      </div>
      {/* Sync Frequency */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span>Sync Frequency</span>
          <span>{registry?.syncFrequency}</span>
        </div>
        <div className="w-full bg-border rounded-full h-1">
          <div 
            className="bg-primary h-1 rounded-full transition-all duration-300"
            style={{ width: `${registry?.syncProgress}%` }}
          />
        </div>
      </div>
      {/* Error Indicator */}
      {registry?.errorCount > 0 && (
        <div className="flex items-center space-x-2 p-2 bg-error/10 border border-error/20 rounded-lg mb-4">
          <Icon name="AlertTriangle" size={16} className="text-error" />
          <span className="text-xs text-error">
            {registry?.errorCount} error{registry?.errorCount > 1 ? 's' : ''} in last 24h
          </span>
        </div>
      )}
      {/* Actions */}
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={() => onForceSync(registry?.id)}
          disabled={registry?.status === 'syncing'}
          className="flex-1"
        >
          Force Sync
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconName="FileText"
          onClick={() => onViewLogs(registry?.id)}
        />
        <Button
          variant="ghost"
          size="sm"
          iconName="Settings"
          onClick={() => onConfigure(registry?.id)}
        />
      </div>
    </div>
  );
};

export default RegistryCard;